// Small building blocks shared by the AI components: a phone shell, a photo with fallback,
// a looping timeline, word-by-word streaming and a few line icons. React + CSS only.
import React, { useEffect, useRef, useState } from 'react';
import { cx, prefersReducedMotion } from '../ui';
import type { HeroUnit } from '../heroUnits';
import { FALLBACK_PHOTO } from './data';
import { AI_CSS } from './styles';

/** Injects the scoped stylesheet once per document. */
export function useAiStyles() {
  useEffect(() => {
    if (document.getElementById('ai-v2-styles')) return;
    const el = document.createElement('style');
    el.id = 'ai-v2-styles';
    el.textContent = AI_CSS;
    document.head.appendChild(el);
  }, []);
}

/** A photo that swaps to a known image if the planned one is not there yet. */
export function Photo({ src, alt, className, width = 1600, height = 1067, eager, style }: { src: string; alt: string; className?: string; width?: number; height?: number; eager?: boolean; style?: React.CSSProperties }) {
  const [s, setS] = useState(src);
  useEffect(() => setS(src), [src]);
  return (
    <img
      src={s}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      style={style}
      onError={() => s !== FALLBACK_PHOTO && setS(FALLBACK_PHOTO)}
    />
  );
}

/** Phone shell. Designed at 300 x 620 and scaled to `width`, so screens stay pixel-consistent. */
export const SCREEN_W = 300;
export const SCREEN_H = 620;
export function Handset({ children, width = 300, className, label }: { children: React.ReactNode; width?: number; className?: string; label?: string }) {
  const k = width / SCREEN_W;
  return (
    <div className={cx('ai-handset-box', className)} style={{ width, height: SCREEN_H * k }} role="img" aria-label={label}>
      <div className="ai-handset" style={{ width: SCREEN_W, height: SCREEN_H, transform: k === 1 ? undefined : `scale(${k})` }}>
        <div className="ai-screen">
          <div className="ai-notch" />
          {children}
        </div>
      </div>
    </div>
  );
}

/** White-label header: the maker's name in its own colour, as the owner sees it. */
export function MakerBar({ unit, small }: { unit: HeroUnit; small?: boolean }) {
  return (
    <div className={cx('ai-maker', small && 'ai-maker-sm')}>
      <span className="ai-maker-mark" style={{ background: unit.accent }}>
        {unit.brand[0]}
      </span>
      <span className="ai-maker-name">{unit.brand}</span>
      <span className="ai-maker-pb">Powered by BrandedMark</span>
    </div>
  );
}

/**
 * Steps through `durations` (ms per step) while `active`. Loops if `loop`; calls onLoop each wrap.
 * Reduced motion: holds the last step (the final frame).
 */
export function useTimeline(durations: number[], active: boolean, opts: { loop?: boolean; onLoop?: () => void; hold?: boolean } = {}) {
  const { loop = true, onLoop, hold = false } = opts;
  const last = durations.length - 1;
  const [reduced] = useState(prefersReducedMotion);
  const [step, setStep] = useState(reduced ? last : 0);
  const onLoopRef = useRef(onLoop);
  onLoopRef.current = onLoop;
  useEffect(() => {
    if (reduced || !active || hold) return;
    const t = window.setTimeout(() => {
      if (step < last) setStep(step + 1);
      else if (loop) {
        onLoopRef.current?.();
        setStep(0);
      }
    }, durations[step]);
    return () => window.clearTimeout(t);
  }, [step, active, hold, reduced, last, loop, durations]);
  return [reduced ? last : step, setStep, reduced] as const;
}

/** Reveals `text` word by word while `running`. Returns the visible text. */
export function useStream(text: string, running: boolean, done: boolean, msPerWord = 55) {
  const words = text.split(' ');
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!running) {
      setN(0);
      return;
    }
    setN(0);
    let i = 0;
    const t = window.setInterval(() => {
      i += 1;
      setN(i);
      if (i >= words.length) window.clearInterval(t);
    }, msPerWord);
    return () => window.clearInterval(t);
  }, [running, text, words.length, msPerWord]);
  if (done) return { shown: text, complete: true };
  if (!running) return { shown: '', complete: false };
  return { shown: words.slice(0, n).join(' '), complete: n >= words.length };
}

/** True while the element is on screen (pauses loops off screen). */
export function useOnScreen<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, on] as const;
}

/* ---------- line icons (stroke, currentColor, 16px) ---------- */
const I = ({ d, size = 14 }: { d: React.ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
export const IconDoc = (p: { size?: number }) => <I {...p} d={<><path d="M4 1.8h5.2L12.5 5v9.2H4z" /><path d="M9 2v3.3h3.3M6.2 8.3h4M6.2 10.8h4" /></>} />;
export const IconPerson = (p: { size?: number }) => <I {...p} d={<><circle cx="8" cy="5.4" r="2.6" /><path d="M2.8 14c.6-2.6 2.7-4.1 5.2-4.1s4.6 1.5 5.2 4.1" /></>} />;
export const IconCheck = (p: { size?: number }) => <I {...p} d={<path d="M3 8.4 6.4 11.6 13 4.6" />} />;
export const IconShield = (p: { size?: number }) => <I {...p} d={<><path d="M8 1.8 13 3.6v4.1c0 3.2-2.1 5.4-5 6.5-2.9-1.1-5-3.3-5-6.5V3.6z" /><path d="M5.6 8 7.3 9.7 10.5 6.4" /></>} />;
export const IconTag = (p: { size?: number }) => <I {...p} d={<><path d="M2 8.6V2.2h6.4L14 7.8 8.4 13.4z" /><circle cx="5.2" cy="5.2" r="1" /></>} />;
export const IconWrench = (p: { size?: number }) => <I {...p} d={<path d="M10.2 2.2a3.3 3.3 0 0 0-3.9 4.4L2.2 10.7a1.5 1.5 0 0 0 2.1 2.1l4.1-4.1a3.3 3.3 0 0 0 4.4-3.9l-2 2-1.9-.4-.4-1.9z" />} />;
export const IconSend = (p: { size?: number }) => <I {...p} d={<path d="M2.4 8h10.4M8.6 3.6 13 8l-4.4 4.4" />} />;
export const IconCalendar = (p: { size?: number }) => <I {...p} d={<><rect x="2.2" y="3" width="11.6" height="10.8" rx="2" /><path d="M2.2 6.6h11.6M5.4 1.6v2.6M10.6 1.6v2.6" /></>} />;
export const IconArrowR = (p: { size?: number }) => <I {...p} d={<path d="M3 8h9.5M8.5 3.5 13 8l-4.5 4.5" />} />;
export const IconLoop = (p: { size?: number }) => <I {...p} d={<><path d="M13 6.2A5.2 5.2 0 0 0 3.4 5" /><path d="M3 2.4V5.2h2.8M3 9.8A5.2 5.2 0 0 0 12.6 11" /><path d="M13 13.6v-2.8h-2.8" /></>} />;
